import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  MessageSquare, X, Send, Loader2, Trash2, Sparkles, Mic, MicOff,
  Paperclip, Briefcase, Compass, Rocket, ChevronRight, ArrowRight, PenTool, Eye,
  Headset, Ticket, Megaphone, BarChart3, CheckCircle2, AlertCircle,
  Shield, Quote,
} from "lucide-react";
import { useWalkthrough } from "../WalkthroughOverlay";
import { api } from "../../lib/api";
import { useLanguage } from "../../contexts/LanguageContext";
import { useGroundedSend } from "../../lib/useGroundedSend";

const MODE_CONFIG = {
  business: {
    icon: Briefcase,
    label: "Business Intel",
    label_es: "Inteligencia de Negocio",
    label_de: "Business Intelligence",
    color: "violet",
    gradient: "from-violet-500/15 to-blue-500/10",
    accent: "violet",
  },
  system: {
    icon: Compass,
    label: "System Guide",
    label_es: "Guia del Sistema",
    label_de: "System-Guide",
    color: "emerald",
    gradient: "from-emerald-500/15 to-teal-500/10",
    accent: "emerald",
  },
  launch: {
    icon: Rocket,
    label: "Launch Advisor",
    label_es: "Asesor de Lanzamiento",
    label_de: "Launch-Berater",
    color: "amber",
    gradient: "from-amber-500/15 to-rose-500/10",
    accent: "amber",
  },
  proposal: {
    icon: PenTool,
    label: "Proposal Expert",
    label_es: "Experto en Propuestas",
    label_de: "Angebots-Experte",
    color: "rose",
    gradient: "from-rose-500/15 to-pink-500/10",
    accent: "rose",
  },
  marketing: {
    icon: Megaphone,
    label: "Marketing Intel",
    label_es: "Inteligencia de Marketing",
    label_de: "Marketing Intelligence",
    color: "pink",
    gradient: "from-pink-500/15 to-fuchsia-500/10",
    accent: "pink",
  },
  analytics: {
    icon: BarChart3,
    label: "Analytics",
    label_es: "Analítica",
    label_de: "Analytik",
    color: "sky",
    gradient: "from-sky-500/15 to-indigo-500/10",
    accent: "sky",
  },
  support: {
    icon: Headset,
    label: "Support Agent",
    label_es: "Agente de Soporte",
    label_de: "Support-Agent",
    color: "cyan",
    gradient: "from-cyan-500/15 to-sky-500/10",
    accent: "cyan",
  },
};

export function AiChat({ grounded: groundedDefault = false } = {}) {
  const { language, t, ti } = useLanguage();
  const location = useLocation();
  const walkthrough = useWalkthrough();
  // ti handles inline translations
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [currentMode, setCurrentMode] = useState("business");
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  // Sprint 942 — service_inspector mode context (passed to backend for guardrail routing)
  const [serviceId, setServiceId] = useState(null);
  // G.6 — per-conversation grounding toggle (super-admin only).
  // groundedDefault is the opt-in seed; the user can flip it from the header.
  const [groundedOn, setGroundedOn] = useState(!!groundedDefault);
  const { groundedSend, groundedDisabled } = useGroundedSend({
    surface: "ai_chat",
    conversationId: sessionId || undefined,
  });
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  // Sprint 942 — listen for global "ai-chat:ask" events so any page can drop a
  // pre-filled prompt + mode into the chat (e.g. Service Tracking dormant pill).
  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail || {};
      if (detail.mode) setCurrentMode(detail.mode);
      if (detail.service_id !== undefined) setServiceId(detail.service_id);
      if (detail.prefill) setInput(detail.prefill);
      setOpen(true);
      setTimeout(() => inputRef.current?.focus(), 100);
    };
    window.addEventListener("ai-chat:ask", handler);
    return () => window.removeEventListener("ai-chat:ask", handler);
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendStreaming = useCallback(
    async (msg) => {
      if (!msg || loading) return;
      setInput("");
      setMessages((prev) => [...prev, { role: "user", text: msg }]);
      setLoading(true);

      // G.6 — grounded path: route through /grounded-answer instead of the
      // streaming chat. Single-shot, no SSE. Citations + reasoning_boundary
      // attach to the assistant bubble. Refusals render with rose styling.
      if (groundedOn && !groundedDisabled) {
        try {
          const g = await groundedSend(msg, { top_k: 5 });
          if (g.fallback) {
            // 401/403 — silently fall back to normal chat path
            setGroundedOn(false);
          } else {
            setMessages((prev) => [
              ...prev,
              {
                role: "assistant",
                text: g.content || "",
                streaming: false,
                grounded: true,
                refused: !!g.refused,
                confidence: g.confidence,
                citations: g.citations || [],
                reasoning_boundary: g.reasoning_boundary,
                top_score: g.top_score,
                run_id: g.run_id,
              },
            ]);
            setLoading(false);
            return;
          }
        } catch {
          // fall through to streaming path
        }
      }

      // Add placeholder for streaming response
      setMessages((prev) => [
        ...prev,
        { role: "assistant", text: "", streaming: true, mode: null, actions: [] },
      ]);

      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem("auth_token");
        const resp = await fetch(`${backendUrl}/api/command-hub/stream`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            message: msg,
            session_id: sessionId,
            language,
            page_context: location.pathname,
            mode: currentMode || undefined,
            service_id: serviceId || undefined,
          }),
        });

        if (!resp.ok) {
          const errData = await resp.json().catch(() => ({}));
          throw new Error(errData.detail || "Stream failed");
        }

        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let accumulated = "";
        let detectedMode = null;
        let newSessionId = sessionId;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const text = decoder.decode(value, { stream: true });
          const lines = text.split("\n").filter((l) => l.startsWith("data: "));

          for (const line of lines) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.session_id && !newSessionId) {
                newSessionId = data.session_id;
                setSessionId(newSessionId);
              }
              if (data.mode && !detectedMode) {
                detectedMode = data.mode;
                setCurrentMode(data.mode);
              }
              if (data.token) {
                accumulated += data.token;
                setMessages((prev) => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last?.role === "assistant") {
                    updated[updated.length - 1] = {
                      ...last,
                      text: accumulated,
                      mode: detectedMode,
                    };
                  }
                  return updated;
                });
              }
              if (data.done) {
                setMessages((prev) => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last?.role === "assistant") {
                    updated[updated.length - 1] = {
                      ...last,
                      streaming: false,
                      actions: data.actions || [],
                      walkthrough: data.walkthrough || null,
                      ticket: data.ticket || null,
                      executed_action: data.executed_action || null,
                      routing: data.routing || null,
                      confidence: data.confidence || null,
                    };
                  }
                  return updated;
                });
              }
              if (data.error) throw new Error(data.error);
            } catch (parseErr) {
              if (parseErr.message !== "Unexpected end of JSON input")
                console.warn("SSE parse:", parseErr);
            }
          }
        }
      } catch (e) {
        // Fallback to non-streaming
        try {
          const r = await api.post("/command-hub/chat", {
            message: msg,
            session_id: sessionId,
            language,
            page_context: location.pathname,
            mode: currentMode || undefined,
            service_id: serviceId || undefined,
          });
          if (r.data.session_id && !sessionId) setSessionId(r.data.session_id);
          if (r.data.mode) setCurrentMode(r.data.mode);
          setMessages((prev) => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last?.role === "assistant") {
              updated[updated.length - 1] = {
                role: "assistant",
                text: r.data.reply,
                streaming: false,
                mode: r.data.mode,
                actions: r.data.actions || [],
                walkthrough: r.data.walkthrough || null,
                ticket: r.data.ticket || null,
                executed_action: r.data.executed_action || null,
                routing: r.data.routing || null,
                confidence: r.data.confidence || null,
              };
            }
            return updated;
          });
        } catch (fallbackErr) {
          setMessages((prev) => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last?.role === "assistant") {
              updated[updated.length - 1] = {
                role: "assistant",
                text: ti(
                  "Sorry, I encountered an error. Please try again.",
                  "Lo siento, ocurrio un error. Intenta de nuevo.",
                  "Entschuldigung, ein Fehler ist aufgetreten. Bitte versuche es erneut."
                ),
                streaming: false,
              };
            }
            return updated;
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [loading, sessionId, language, location.pathname, t, currentMode, serviceId,
     groundedOn, groundedDisabled, groundedSend]
  );

  const send = () => sendStreaming(input.trim());

  const clear = async () => {
    if (sessionId) {
      try {
        await api.post("/command-hub/clear", { session_id: sessionId });
      } catch {}
    }
    setMessages([]);
    setSessionId("");
    setCurrentMode("business");
  };

  // Voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "audio/mp4",
      });
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType });
        await transcribeAudio(blob, mr.mimeType.includes("webm") ? "webm" : "mp4");
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch (err) {
      console.error("Mic access denied:", err);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const transcribeAudio = async (blob, ext) => {
    setTranscribing(true);
    try {
      const formData = new FormData();
      formData.append("file", blob, `voice.${ext}`);
      const r = await api.post("/ai-features/assistant/voice", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (r.data.text) {
        setInput(r.data.text);
        if (inputRef.current) inputRef.current.focus();
      }
    } catch (e) {
      console.error("Transcription error:", e);
    } finally {
      setTranscribing(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isImage = file.type.startsWith("image/");
    const description = isImage
      ? ti(
          `[Attached image: ${file.name}] Please analyze this image.`,
          `[Imagen adjunta: ${file.name}] Por favor analiza esta imagen.`,
          `[Angehängtes Bild: ${file.name}] Bitte analysiere dieses Bild.`
        )
      : ti(
          `[Attached file: ${file.name}, size: ${(file.size / 1024).toFixed(1)}KB] Please analyze this file.`,
          `[Archivo adjunto: ${file.name}, tamano: ${(file.size / 1024).toFixed(1)}KB] Por favor analiza este archivo.`,
          `[Angehängte Datei: ${file.name}, Größe: ${(file.size / 1024).toFixed(1)}KB] Bitte analysiere diese Datei.`
        );
    setInput((prev) => (prev ? `${prev}\n${description}` : description));
    e.target.value = "";
  };

  // Context-aware quick suggestions based on current page
  const getPageSuggestions = useCallback(() => {
    const path = location.pathname;
    if (path.includes("/leads")) return [
      t("Score my top leads"),
      t("Suggest follow-up actions"),
    ];
    if (path.includes("/projects")) return [
      t("What projects are overdue?"),
      t("Summarize project budgets"),
    ];
    if (path.includes("/proposal-builder") || path.includes("/proposals")) return [
      t("Suggest content for this block"),
      t("Improve proposal writing"),
    ];
    if (path.includes("/invoices") || path.includes("/quotes")) return [
      t("Show unpaid invoices"),
      t("Revenue summary this month"),
    ];
    if (path.includes("/kanban") || path.includes("/pipeline")) return [
      t("Analyze my sales funnel"),
      t("Who needs a follow-up?"),
    ];
    if (path.includes("/marketing") || path.includes("/social")) return [
      t("Suggest campaign ideas"),
      t("Content calendar this week"),
    ];
    if (path.includes("/settings")) return [
      t("How do I configure email?"),
      t("Explain notification settings"),
    ];
    return [];
  }, [location.pathname, t]);

  // Contextual suggestions based on current mode + page
  const pageSuggestions = getPageSuggestions();
  const suggestions =
    currentMode === "system"
      ? [
          t("How do I create a quote?"),
          t("Where is the ROI tracker?"),
          t("Explain the sales pipeline"),
          t("How does Brand Launch work?"),
        ]
      : currentMode === "launch"
        ? [
            t("Analyze my gaps"),
            t("Help me write a brand voice"),
            t("What should I improve first?"),
            t("How does ROI tracking work?"),
          ]
        : currentMode === "proposal"
          ? [
              t("Write an About Us section"),
              t("Improve my pricing pitch"),
              t("Suggest a proposal structure"),
              t("Analyze proposal engagement"),
            ]
          : currentMode === "support"
            ? [
                t("Report an issue"),
                t("Something is not working"),
                t("Check my ticket status"),
                t("The page won't load"),
              ]
            : currentMode === "marketing"
              ? [
                  t("How are my social posts performing?"),
                  t("Analyze email campaign open rates"),
                  t("Suggest content for this week"),
                  t("What's my SEO score?"),
                ]
              : currentMode === "analytics"
                ? [
                    t("How are we doing this month?"),
                    t("Show conversion rate trends"),
                    t("Revenue vs last month"),
                    t("What needs my attention?"),
                  ]
                : [
                    t("What projects are overdue?"),
                    t("Show me pending invoices"),
                    t("Who's available this week?"),
                    t("Revenue summary this month"),
                  ];

  const modeConfig = MODE_CONFIG[currentMode] || MODE_CONFIG.business;
  const ModeIcon = modeConfig.icon;

  const accentClasses = {
    violet: {
      bg: "bg-violet-600",
      bgHover: "hover:bg-violet-500",
      bgLight: "bg-violet-500/10",
      text: "text-violet-400",
      border: "border-violet-500/20",
      bubble: "bg-violet-600",
      pulse: "bg-violet-400",
    },
    emerald: {
      bg: "bg-emerald-600",
      bgHover: "hover:bg-emerald-500",
      bgLight: "bg-emerald-500/10",
      text: "text-emerald-400",
      border: "border-emerald-500/20",
      bubble: "bg-emerald-600",
      pulse: "bg-emerald-400",
    },
    amber: {
      bg: "bg-amber-600",
      bgHover: "hover:bg-amber-500",
      bgLight: "bg-amber-500/10",
      text: "text-amber-400",
      border: "border-amber-500/20",
      bubble: "bg-amber-500",
      pulse: "bg-amber-400",
    },
    rose: {
      bg: "bg-rose-600",
      bgHover: "hover:bg-rose-500",
      bgLight: "bg-rose-500/10",
      text: "text-rose-400",
      border: "border-rose-500/20",
      bubble: "bg-rose-600",
      pulse: "bg-rose-400",
    },
    cyan: {
      bg: "bg-cyan-600",
      bgHover: "hover:bg-cyan-500",
      bgLight: "bg-cyan-500/10",
      text: "text-cyan-400",
      border: "border-cyan-500/20",
      bubble: "bg-cyan-600",
      pulse: "bg-cyan-400",
    },
    pink: {
      bg: "bg-pink-600",
      bgHover: "hover:bg-pink-500",
      bgLight: "bg-pink-500/10",
      text: "text-pink-400",
      border: "border-pink-500/20",
      bubble: "bg-pink-600",
      pulse: "bg-pink-400",
    },
    sky: {
      bg: "bg-sky-600",
      bgHover: "hover:bg-sky-500",
      bgLight: "bg-sky-500/10",
      text: "text-sky-400",
      border: "border-sky-500/20",
      bubble: "bg-sky-600",
      pulse: "bg-sky-400",
    },
  };

  const ac = accentClasses[modeConfig.accent] || accentClasses.violet;

  return (
    <>
      {/* Floating trigger */}
      <button
        onClick={() => setOpen(!open)}
        className={`fixed bottom-5 right-5 z-50 w-12 h-12 rounded-full flex items-center justify-center shadow-xl transition-all ${
          open
            ? "bg-red-500 hover:bg-red-600 scale-90"
            : `${ac.bg} ${ac.bgHover} scale-100 hover:scale-110`
        }`}
        data-testid="ai-chat-toggle"
      >
        {open ? (
          <X className="w-5 h-5 text-white" />
        ) : (
          <Sparkles className="w-5 h-5 text-white" />
        )}
      </button>

      {open && (
        <div
          className="fixed bottom-20 right-5 z-50 w-[400px] max-h-[560px] rounded-2xl shadow-2xl border border-[var(--app-border)] overflow-hidden flex flex-col"
          style={{ backgroundColor: "var(--app-bg)" }}
          data-testid="ai-chat-panel"
        >
          {/* Header */}
          <div
            className={`flex items-center justify-between px-4 py-3 border-b border-[var(--app-border)]`}
            style={{
              background: `linear-gradient(135deg, ${
                modeConfig.accent === "violet"
                  ? "rgba(139,92,246,0.15), rgba(59,130,246,0.1)"
                  : modeConfig.accent === "emerald"
                    ? "rgba(16,185,129,0.15), rgba(20,184,166,0.1)"
                    : "rgba(245,158,11,0.15), rgba(244,63,94,0.1)"
              })`,
            }}
          >
            <div className="flex items-center gap-2">
              <div
                className={`w-7 h-7 rounded-full ${ac.bg} flex items-center justify-center`}
              >
                <ModeIcon className="w-3.5 h-3.5 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-[var(--app-text)]">
                  {t("Command Hub")}
                </p>
                <p className="text-[10px] text-[var(--app-text-muted)]">
                  {language === "de" ? (modeConfig.label_de || modeConfig.label) : language === "es" ? modeConfig.label_es : modeConfig.label}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {/* Mode pills */}
              {Object.entries(MODE_CONFIG).map(([id, cfg]) => {
                const Icon = cfg.icon;
                const isActive = currentMode === id;
                return (
                  <button
                    key={id}
                    onClick={() => setCurrentMode(id)}
                    className={`p-1.5 rounded-lg transition-all ${
                      isActive
                        ? `${accentClasses[cfg.accent]?.bgLight} ${accentClasses[cfg.accent]?.text}`
                        : "text-[var(--app-text-muted)] hover:text-[var(--app-text)] hover:bg-white/[0.06]"
                    }`}
                    title={language === "de" ? (cfg.label_de || cfg.label) : language === "es" ? cfg.label_es : cfg.label}
                    data-testid={`mode-${id}`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                  </button>
                );
              })}
              <div className="w-px h-4 bg-[var(--app-border)] mx-1" />
              {!groundedDisabled && (
                <button
                  onClick={() => setGroundedOn((v) => !v)}
                  className={`p-1.5 rounded-lg transition-colors ${
                    groundedOn
                      ? "bg-indigo-500/15 text-indigo-300"
                      : "text-[var(--app-text-muted)] hover:text-[var(--app-text)] hover:bg-white/[0.06]"
                  }`}
                  title={
                    groundedOn
                      ? t("Grounded mode ON — replies cite Compass canon")
                      : t("Enable grounded mode (Compass-cited replies)")
                  }
                  data-testid="ai-chat-grounded-toggle"
                >
                  <Shield className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                onClick={clear}
                className="text-[var(--app-text-muted)] hover:text-[var(--app-text)] p-1.5 rounded-lg hover:bg-white/[0.06] transition-colors"
                data-testid="ai-chat-clear"
                title={t("Clear conversation")}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-[280px] max-h-[380px]">
            {messages.length === 0 && (
              <div className="space-y-3 pt-2">
                <p className="text-xs text-[var(--app-text-muted)] text-center">
                  {currentMode === "system"
                    ? ti(
                        "Ask me how to use any feature in the system.",
                        "Preguntame como usar cualquier funcion del sistema.",
                        "Frag mich, wie du eine Funktion im System nutzen kannst."
                      )
                    : currentMode === "launch"
                      ? ti(
                          "Ask me about your brand launch readiness.",
                          "Preguntame sobre tu preparación para el lanzamiento.",
                          "Frag mich nach deiner Markenstart-Bereitschaft."
                        )
                      : currentMode === "proposal"
                        ? ti(
                            "I'll help you write, improve, and analyze proposals.",
                            "Te ayudare a escribir, mejorar y analizar propuestas.",
                            "Ich helfe dir beim Schreiben, Verbessern und Analysieren von Angeboten."
                          )
                        : currentMode === "support"
                          ? ti(
                              "Report an issue or ask about your ticket status.",
                              "Reporta un problema o pregunta por el estado de tu ticket.",
                              "Melde ein Problem oder frage nach deinem Ticket-Status."
                            )
                          : currentMode === "marketing"
                            ? ti(
                                "Ask about your social media, email campaigns, or SEO performance.",
                                "Pregunta sobre tus redes sociales, campañas de email o rendimiento SEO.",
                                "Frag nach deinen Social Media, E-Mail-Kampagnen oder SEO-Performance."
                              )
                            : currentMode === "analytics"
                              ? ti(
                                  "Get cross-module insights, trends, and business intelligence.",
                                  "Obtén insights transversales, tendencias e inteligencia de negocio.",
                                  "Erhalte modulübergreifende Einblicke, Trends und Business Intelligence."
                                )
                              : ti(
                                  "Ask me anything about your projects, budgets, crew, or finances.",
                                  "Preguntame sobre tus proyectos, presupuestos, crew o finanzas.",
                                  "Frag mich alles über deine Projekte, Budgets, Crew oder Finanzen."
                                )}
                </p>
                <div className="grid grid-cols-2 gap-1.5">
                  {suggestions.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => setInput(s)}
                      className={`text-left p-2 rounded-lg text-[10px] text-[var(--app-text-muted)] border border-[var(--app-border)] hover:${ac.border} hover:${ac.bgLight} transition-colors`}
                      data-testid={`ai-suggestion-${i}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                {pageSuggestions.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-[var(--app-border)]">
                    <p className="text-[9px] uppercase tracking-wider text-[var(--app-text-muted)] mb-1.5 flex items-center gap-1">
                      <ChevronRight className="w-2.5 h-2.5" />
                      {t("Based on this page")}
                    </p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {pageSuggestions.map((s, i) => (
                        <button key={`ctx-${i}`} onClick={() => { setInput(s); }}
                          className={`text-left p-2 rounded-lg text-[10px] font-medium ${ac.text} border ${ac.border} ${ac.bgLight} transition-colors`}
                          data-testid={`ai-ctx-suggestion-${i}`}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div className="max-w-[85%]">
                  {/* G.6 — grounded badge for assistant messages */}
                  {m.role === "assistant" && m.grounded && (
                    <span
                      className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded mb-1"
                      style={{
                        background: m.refused ? "rgba(244,63,94,0.14)" : "rgba(99,102,241,0.14)",
                        color: m.refused ? "#fb7185" : "#a5b4fc",
                      }}
                      data-testid={`grounded-badge-${i}`}
                    >
                      <Shield className="w-2.5 h-2.5" />
                      {m.refused
                        ? t("Refused")
                        : `${t("Grounded")} · ${m.confidence || "—"}`}
                    </span>
                  )}
                  {/* Mode badge for assistant messages */}
                  {m.role === "assistant" && m.mode && !m.grounded && (
                    <ModeLabel mode={m.mode} language={language} />
                  )}
                  <div
                    className={`px-3 py-2 rounded-xl text-xs leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? `${ac.bubble} text-white rounded-br-sm`
                        : m.grounded && m.refused
                          ? "rounded-bl-sm border"
                          : "bg-[var(--app-surface-hover)] text-[var(--app-text)] rounded-bl-sm border border-[var(--app-border)]"
                    }`}
                    style={
                      m.grounded && m.refused
                        ? {
                            background: "rgba(244,63,94,0.05)",
                            borderColor: "rgba(244,63,94,0.3)",
                            color: "#fda4af",
                          }
                        : undefined
                    }
                    data-testid={`chat-msg-${i}`}
                  >
                    {m.text}
                    {m.streaming && (
                      <span
                        className={`inline-block w-1.5 h-3.5 ${ac.pulse} animate-pulse ml-0.5 rounded-sm`}
                      />
                    )}
                  </div>
                  {/* G.6 — reasoning_boundary + citations for grounded answers */}
                  {m.grounded && !m.refused && m.reasoning_boundary && (
                    <div className="mt-1 px-2 py-1 text-[10px] italic text-[var(--app-text-muted)] flex items-start gap-1">
                      <Quote className="w-2.5 h-2.5 mt-0.5 flex-shrink-0 text-indigo-300" />
                      <span>{m.reasoning_boundary}</span>
                    </div>
                  )}
                  {m.grounded && !m.refused && (m.citations || []).length > 0 && (
                    <details className="mt-1" data-testid={`grounded-citations-${i}`}>
                      <summary className="text-[10px] text-indigo-300 cursor-pointer hover:text-indigo-200">
                        {m.citations.length}{" "}
                        {m.citations.length === 1 ? t("citation") : t("citations")}
                        {m.top_score != null && (
                          <span className="ml-1 text-[var(--app-text-muted)] font-mono">
                            · top {m.top_score.toFixed(3)}
                          </span>
                        )}
                      </summary>
                      <ul className="mt-1 space-y-1 pl-3">
                        {m.citations.map((c, j) => (
                          <li
                            key={j}
                            className="text-[10px] text-[var(--app-text-secondary)]"
                          >
                            <span className="font-mono text-indigo-300 mr-1">
                              [{c.chunk_id?.slice(0, 8)}]
                            </span>
                            <span className="italic">“{c.quote}”</span>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                  {/* Action buttons */}
                  {m.actions?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {m.actions.map((action, j) => (
                        <a
                          key={j}
                          href={action.path}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-md ${ac.bgLight} ${ac.text} text-[10px] font-medium hover:opacity-80 transition-opacity`}
                          data-testid={`action-btn-${j}`}
                        >
                          {action.label}{" "}
                          <ArrowRight className="w-2.5 h-2.5" />
                        </a>
                      ))}
                    </div>
                  )}
                  {/* Show Me walkthrough button */}
                  {m.walkthrough?.length > 0 && !m.streaming && (
                    <button
                      onClick={() => { walkthrough.start(m.walkthrough); setOpen(false); }}
                      className="mt-1.5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--app-accent)] text-white text-[10px] font-semibold hover:bg-[var(--app-accent-hover)] transition-all animate-in fade-in slide-in-from-bottom-1"
                      data-testid="walkthrough-show-me-btn"
                    >
                      <Eye className="w-3 h-3" /> {t("Show Me")}
                    </button>
                  )}
                  {/* Ticket creation card */}
                  {m.ticket && (
                    <div className="mt-2 p-2.5 rounded-lg bg-cyan-500/10 border border-cyan-500/20" data-testid={`ticket-card-${m.ticket.ticket_id}`}>
                      <div className="flex items-center gap-1.5 mb-1">
                        <Ticket className="w-3 h-3 text-cyan-400" />
                        <span className="text-[10px] font-bold text-cyan-400">{m.ticket.ticket_id}</span>
                        <span className={`ml-auto text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${
                          m.ticket.priority === "critical" ? "bg-red-500/20 text-red-400" :
                          m.ticket.priority === "high" ? "bg-amber-500/20 text-amber-400" :
                          "bg-cyan-500/15 text-cyan-400"
                        }`}>{m.ticket.priority}</span>
                      </div>
                      <p className="text-[11px] font-medium text-[var(--app-text)]">{m.ticket.subject}</p>
                      <p className="text-[9px] text-[var(--app-text-muted)] mt-0.5">{t("Ticket created — admin team will review")}</p>
                    </div>
                  )}
                  {/* Action execution result */}
                  {m.executed_action && (
                    <div className={`mt-2 p-2.5 rounded-lg border ${m.executed_action.success ? "bg-emerald-500/10 border-emerald-500/20" : "bg-red-500/10 border-red-500/20"}`} data-testid="action-result-card">
                      <div className="flex items-center gap-1.5">
                        {m.executed_action.success ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                        ) : (
                          <AlertCircle className="w-3.5 h-3.5 text-red-400" />
                        )}
                        <span className={`text-[11px] font-semibold ${m.executed_action.success ? "text-emerald-400" : "text-red-400"}`}>
                          {m.executed_action.success ? t("Action Executed") : t("Action Failed")}
                        </span>
                        <span className="ml-auto text-[9px] font-mono text-[var(--app-text-muted)]">
                          {m.executed_action.action}
                        </span>
                      </div>
                      <p className="text-[10px] text-[var(--app-text)] mt-1">{m.executed_action.message}</p>
                    </div>
                  )}
                  {/* AI Routing Transparency */}
                  {m.routing && !m.streaming && (
                    <details className="mt-2 group" data-testid="routing-transparency">
                      <summary className="flex items-center gap-1.5 cursor-pointer text-[9px] text-[var(--app-text-muted)] hover:text-[var(--app-text)] transition-colors select-none">
                        <Sparkles className="w-2.5 h-2.5" />
                        <span>
                          {t("Routed to")} <span className={`font-semibold ${ac.text}`}>{MODE_CONFIG[m.mode || currentMode]?.label || m.mode}</span>
                        </span>
                        {m.confidence != null && (
                          <span className={`ml-1 px-1.5 py-px rounded-full text-[7px] font-bold ${m.confidence >= 0.7 ? "bg-emerald-500/15 text-emerald-400" : m.confidence >= 0.4 ? "bg-amber-500/15 text-amber-400" : "bg-red-500/15 text-red-400"}`}>
                            {Math.round(m.confidence * 100)}%
                          </span>
                        )}
                        <ChevronRight className="w-2.5 h-2.5 transition-transform group-open:rotate-90" />
                      </summary>
                      <div className="mt-1.5 px-2.5 py-2 rounded-lg bg-white/[0.03] border border-[var(--app-border)] text-[9px] space-y-2" data-testid="routing-details">
                        {/* Reason */}
                        <div>
                          <p className="text-[8px] font-bold text-[var(--app-text-muted)] uppercase tracking-wider mb-0.5">{t("Why this agent")}</p>
                          <p className="text-[var(--app-text)] leading-relaxed">{m.routing.reason}</p>
                        </div>
                        {/* Matched Keywords */}
                        {m.routing.matched_keywords?.length > 0 && (
                          <div>
                            <p className="text-[8px] font-bold text-[var(--app-text-muted)] uppercase tracking-wider mb-1">{t("Trigger keywords")}</p>
                            <div className="flex items-center gap-1 flex-wrap">
                              {m.routing.matched_keywords.map((kw, ki) => (
                                <span key={ki} className={`px-1.5 py-0.5 rounded text-[8px] font-medium ${ac.bgLight} ${ac.text}`}>{kw}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Confidence Bar */}
                        {m.confidence != null && (
                          <div>
                            <p className="text-[8px] font-bold text-[var(--app-text-muted)] uppercase tracking-wider mb-1">{t("Confidence")}</p>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
                                <div className={`h-full rounded-full transition-all ${m.confidence >= 0.7 ? "bg-emerald-500" : m.confidence >= 0.4 ? "bg-amber-500" : "bg-red-500"}`}
                                  style={{ width: `${Math.round(m.confidence * 100)}%` }} />
                              </div>
                              <span className="text-[8px] font-mono text-[var(--app-text-muted)] w-7 text-right">{Math.round(m.confidence * 100)}%</span>
                            </div>
                          </div>
                        )}
                        {/* Competing Scores */}
                        {m.routing.all_scores && Object.keys(m.routing.all_scores).length > 1 && (
                          <div>
                            <p className="text-[8px] font-bold text-[var(--app-text-muted)] uppercase tracking-wider mb-1">{t("Agent scores")}</p>
                            <div className="space-y-1">
                              {Object.entries(m.routing.all_scores).map(([mode, score]) => {
                                const maxScore = Math.max(...Object.values(m.routing.all_scores));
                                const pct = maxScore > 0 ? (score / maxScore) * 100 : 0;
                                const isWinner = mode === (m.mode || currentMode);
                                const modeConf = MODE_CONFIG[mode];
                                const ModeIcon = modeConf?.icon || Sparkles;
                                return (
                                  <div key={mode} className="flex items-center gap-1.5">
                                    <ModeIcon className={`w-2.5 h-2.5 shrink-0 ${isWinner ? ac.text : "text-[var(--app-text-faint)]"}`} />
                                    <span className={`text-[8px] w-14 truncate ${isWinner ? "font-semibold text-[var(--app-text)]" : "text-[var(--app-text-muted)]"}`}>
                                      {modeConf?.label || mode}
                                    </span>
                                    <div className="flex-1 h-1 rounded-full bg-white/[0.05] overflow-hidden">
                                      <div className={`h-full rounded-full ${isWinner ? "bg-violet-500" : "bg-white/10"}`} style={{ width: `${pct}%` }} />
                                    </div>
                                    <span className="text-[8px] font-mono text-[var(--app-text-faint)] w-5 text-right">{score}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </details>
                  )}
                </div>
              </div>
            ))}
            {loading && messages[messages.length - 1]?.role !== "assistant" && (
              <div className="flex justify-start">
                <div className="bg-[var(--app-surface-hover)] border border-[var(--app-border)] rounded-xl rounded-bl-sm px-3 py-2 flex items-center gap-2">
                  <Loader2 className={`w-3.5 h-3.5 animate-spin ${ac.text}`} />
                  <span className="text-xs text-[var(--app-text-muted)]">
                    {t("Thinking...")}
                  </span>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div
            className="px-3 py-2.5 border-t border-[var(--app-border)]"
            style={{ backgroundColor: "var(--app-surface)" }}
          >
            {(recording || transcribing) && (
              <div className="flex items-center gap-2 mb-2 px-1">
                {recording && (
                  <>
                    <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                    <span className="text-[10px] text-red-400 font-medium">
                      {t("Recording... tap to stop")}
                    </span>
                  </>
                )}
                {transcribing && (
                  <>
                    <Loader2 className={`w-3 h-3 animate-spin ${ac.text}`} />
                    <span className={`text-[10px] ${ac.text} font-medium`}>
                      {t("Transcribing...")}
                    </span>
                  </>
                )}
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <label
                className="cursor-pointer text-[var(--app-text-muted)] hover:text-[var(--app-text)] p-1 transition-colors"
                data-testid="ai-chat-attach"
              >
                <Paperclip className="w-4 h-4" />
                <input
                  type="file"
                  className="hidden"
                  onChange={handleFileUpload}
                  accept="image/*,.pdf,.txt,.csv,.xlsx,.docx"
                />
              </label>
              <button
                onClick={recording ? stopRecording : startRecording}
                disabled={transcribing}
                className={`p-1 rounded-full transition-all ${
                  recording
                    ? "bg-red-500 text-white animate-pulse"
                    : "text-[var(--app-text-muted)] hover:text-[var(--app-text)]"
                } disabled:opacity-40`}
                data-testid="ai-chat-voice"
              >
                {recording ? (
                  <MicOff className="w-4 h-4" />
                ) : (
                  <Mic className="w-4 h-4" />
                )}
              </button>
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder={
                  currentMode === "system"
                    ? t("Ask how to use any feature...")
                    : currentMode === "launch"
                      ? t("Ask about your launch readiness...")
                      : currentMode === "proposal"
                        ? t("Write, improve, or analyze proposals...")
                        : currentMode === "support"
                          ? t("Describe the issue you're facing...")
                          : t("Ask about projects, crew, budgets...")
                }
                className="flex-1 bg-transparent text-xs text-[var(--app-text)] placeholder:text-[var(--app-text-faint)] outline-none"
                disabled={loading || recording}
                data-testid="ai-chat-input"
              />
              <button
                onClick={send}
                disabled={loading || !input.trim() || recording}
                className={`w-7 h-7 rounded-full ${ac.bg} ${ac.bgHover} disabled:opacity-40 flex items-center justify-center transition-colors`}
                data-testid="ai-chat-send"
              >
                <Send className="w-3.5 h-3.5 text-white" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ModeLabel({ mode, language }) {
  const cfg = MODE_CONFIG[mode];
  if (!cfg) return null;
  const label = language === "de" ? (cfg.label_de || cfg.label) : language === "es" ? cfg.label_es : cfg.label;
  const colorClass =
    mode === "system"
      ? "text-emerald-400 bg-emerald-500/10"
      : mode === "launch"
        ? "text-amber-400 bg-amber-500/10"
        : mode === "proposal"
          ? "text-rose-400 bg-rose-500/10"
          : mode === "support"
            ? "text-cyan-400 bg-cyan-500/10"
            : "text-violet-400 bg-violet-500/10";

  return (
    <span
      className={`inline-block text-[9px] font-medium px-1.5 py-0.5 rounded mb-1 ${colorClass}`}
      data-testid={`mode-label-${mode}`}
    >
      {label}
    </span>
  );
}
